/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import LeonhardWeissLogo from '/src/assets/logos/leonhard-weiss.svg';
import { VITE_NODE_ENV } from '@/utils/getEnv';
import { CheckLogo } from '../logos/CheckLogo';
import GridtyLogo from '/src/assets/logos/gridty_logo.svg';
import { Field } from '@/types';
import { CategoryItem } from '@/shared/MainContainer';
import LeonovaLogo from '/src/assets/logos/leonova.svg';

const clientLogos = {
  'leonhard-weiss': LeonhardWeissLogo,
  gridty: GridtyLogo,
  leonova: LeonovaLogo,
};

const logo = VITE_NODE_ENV['VITE_THEME'] as keyof typeof clientLogos;

export const Sidebar = ({
  page,
  pages,
  formValues,
  isFetched = false,
}: {
  page?: string;
  pages: CategoryItem[];
  formValues: Record<string, any>;
  isFetched?: boolean;
}) => {
  function isEmpty(value: any): boolean {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  function isFilled(val: any) {
    return (
      val !== undefined &&
      val !== null &&
      (typeof val === 'string' ? val.trim().length > 0 : true) &&
      (!Array.isArray(val) || val.length > 0)
    );
  }

  function isEnergyProfileComplete(formValues: Record<string, any>) {
    const hasLoadProfile = formValues.transient?.has_loadprofile;
    const loadProfileType = formValues.load_profile?.type_of_loadprofile;
    const uploaded = formValues.load_profile_upload;

    if (hasLoadProfile === false && isFilled(loadProfileType)) return true;
    if (hasLoadProfile === true && Array.isArray(uploaded) && uploaded.length > 0 && isFilled(loadProfileType))
      return true;

    return false;
  }

  function isNotEmpty(val: any): boolean {
    if (val === undefined || val === null) return false;
    if (typeof val === 'string') return val.trim().length > 0;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object') return Object.keys(val).length > 0;
    return true;
  }

  function isLadesaeulenComplete(formValues: Record<string, any>): boolean {
    const wantsCharging = formValues.transient?.wants_charging;
    const hasPublicParking = formValues.transient?.has_public_parking;
    const fi = formValues.further_information || {};

    if (wantsCharging === false) return true;

    if (
      wantsCharging === true &&
      hasPublicParking === true &&
      isNotEmpty(fi.count_normal_ev) &&
      isNotEmpty(fi.count_fast_ev) &&
      isNotEmpty(fi.count_normal_public_ev) &&
      isNotEmpty(fi.count_fast_public_ev)
    ) {
      return true;
    }

    if (
      wantsCharging === true &&
      hasPublicParking === false &&
      isNotEmpty(fi.count_normal_ev) &&
      isNotEmpty(fi.count_fast_ev)
    ) {
      return true;
    }

    return false;
  }

  function isHeatCategoryComplete(formValues: Record<string, any>): boolean {
    const isHeatRequired = formValues.transient?.is_heat_required;
    const heatInformation = formValues.heat_information;

    if (isHeatRequired === true && isNotEmpty(heatInformation)) return true;

    return false;
  }

  function areRequiredFieldsFilled(qns: Field[] = [], formValues: Record<string, any> = {}): boolean {
    return qns
      .filter(q => q.is_required)
      .every(q => {
        const modelValue = formValues[q.model]?.[q.name];
        const flatValue = formValues[q.name];
        const value = modelValue !== undefined ? modelValue : flatValue;

        if (q.type === 'checkbox') {
          return Boolean(value);
        }
        if (q.type === 'file') {
          return Array.isArray(value) && value.length > 0;
        }
        return !isEmpty(value);
      });
  }

  return (
    <div
      className={`${logo == 'leonova' ? 'border-r border-r-[#FFFFFF33]' : ''} px-[28px] py-[28px] md:py-[20px] bg-secondary max-w-[360px] h-[100vh]`}
    >
      <img
        src={clientLogos[logo]}
        alt="logo"
        className={`${logo == 'leonova' ? 'pl-[50px]' : ''} mb-[54px] md:mb-[30.8px]`}
      />
      <div
        className="flex flex-col gap-[35px] md:gap-[24px] cursor-pointer"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--scrollbar-thumb-color, #888) transparent',
        }}
      >
        {pages?.map((nav, ind) => {
          let pageComplete;
          if (nav.category === 'Ladesäulen') {
            pageComplete = isLadesaeulenComplete(formValues);
          } else if (nav.category === 'Wärmeversorgung') {
            pageComplete = isHeatCategoryComplete(formValues);
          } else if (nav.category === 'Energieprofil') {
            pageComplete = isEnergyProfileComplete(formValues);
          } else {
            const pagesMatch = pages.filter(pageConfig => pageConfig.category === nav.category);
            const allQns = pagesMatch.flatMap(pm => pm.qns ?? []);
            pageComplete = areRequiredFieldsFilled(allQns, formValues);
          }

          if (ind == 7) {
            pageComplete = isFetched;
          }
          return (
            <div key={nav?.name} className="relative">
              <div className="flex gap-[18px] items-center">
                {pageComplete ? <CheckLogo /> : <CheckLogo fill="bg-primary" />}
                <div
                  className={`p-[10px] border-warning bg-warning rounded-[7.5px] ${page == nav?.qns?.[0]?.title || pageComplete ? '' : 'opacity-30'}`}
                >
                  {nav?.qns?.[0]?.icon}
                </div>
                <p
                  className={`text-[16px] !text-primary font-medium ${page == nav?.qns?.[0]?.title || pageComplete ? '' : 'opacity-30'}`}
                >
                  {nav?.qns?.[0]?.title}
                </p>
              </div>
              {ind !== 7 ? (
                <div className="bg-[#FFEB99] h-[16px] rounded-[2px] w-[2px] absolute left-[48px] top-[43px]" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};